import React, { useState, useEffect, useRef } from 'react';
import { Icon } from './Icon.js';
import { supabase } from '../supabase.js';
import './TabPrivacySettings.css';

const TabPrivacySettings = ({ userId, tabName, currentPrivacyLevel, onPrivacyChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [privacyLevel, setPrivacyLevel] = useState(currentPrivacyLevel || 'followers_only');
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setPrivacyLevel(currentPrivacyLevel || 'followers_only');
  }, [currentPrivacyLevel]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close FAB menu on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handlePrivacyChange = async (newPrivacyLevel) => {
    setLoading(true);
    try {
      // First, try to update existing record
      const { data: existingData, error: selectError } = await supabase
        .from('user_tab_privacy')
        .select('*')
        .eq('user_id', userId)
        .eq('tab_name', tabName)
        .single();

      if (existingData) {
        // Update existing record
        const { error: updateError } = await supabase
          .from('user_tab_privacy')
          .update({ privacy_level: newPrivacyLevel })
          .eq('user_id', userId)
          .eq('tab_name', tabName);

        if (updateError) throw updateError;
      } else {
        // Insert new record
        const { error: insertError } = await supabase
          .from('user_tab_privacy')
          .insert({
            user_id: userId,
            tab_name: tabName,
            privacy_level: newPrivacyLevel
          });

        if (insertError) throw insertError;
      }

      setPrivacyLevel(newPrivacyLevel);
      setIsOpen(false);
      
      if (onPrivacyChange) {
        onPrivacyChange(tabName, newPrivacyLevel);
      }
    } catch (error) {
      console.error('Error updating privacy level:', error);
      alert('Failed to update privacy settings. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPrivacyIcon = (level) => {
    switch (level) {
      case 'public':
        return 'Globe';
      case 'followers_only':
        return 'Users';
      case 'private':
        return 'Lock';
      default:
        return 'Globe';
    }
  };

  const getPrivacyLabel = (level) => {
    switch (level) {
      case 'public':
        return 'Public';
      case 'followers_only':
        return 'Followers Only';
      case 'private':
        return 'Private';
      default:
        return 'Public';
    }
  };

  const getPrivacyDescription = (level) => {
    switch (level) {
      case 'public':
        return 'Anyone can view tab';
      case 'followers_only':
        return 'Only your followers can view tab';
      case 'private':
        return 'Only you can view tab';
      default:
        return 'Anyone can view tab';
    }
  };

  // Handle menu item clicks
  const handlePublicClick = (e) => {
    e.stopPropagation();
    handlePrivacyChange('public');
  };

  const handleFollowersOnlyClick = (e) => {
    e.stopPropagation();
    handlePrivacyChange('followers_only');
  };

  const handlePrivateClick = (e) => {
    e.stopPropagation();
    handlePrivacyChange('private');
  };

  return (
    <div className="tab-privacy-settings" ref={dropdownRef}>
      {/* Original Button */}
      <button
        className="tab-privacy-toggle"
        onClick={() => setIsOpen(!isOpen)}
        title={`Privacy: ${getPrivacyLabel(privacyLevel)}`}
        disabled={loading}
      >
        <Icon name={getPrivacyIcon(privacyLevel)} className="tab-privacy-icon" />
        <Icon name="ChevronDown" className="tab-privacy-chevron" />
      </button>

      {/* FAB Menu */}
      {isOpen && (
        <div 
          className="tab-privacy-fab-menu"
          role="menu"
          aria-orientation="vertical"
        >
          <button
            className={`tab-privacy-fab-menu-item ${privacyLevel === 'public' ? 'active' : ''}`}
            onClick={handlePublicClick}
            disabled={loading}
            role="menuitem"
          >
            <Icon name="Globe" className="tab-privacy-fab-menu-icon" />
            <span>Public</span>
          </button>
          <button
            className={`tab-privacy-fab-menu-item ${privacyLevel === 'followers_only' ? 'active' : ''}`}
            onClick={handleFollowersOnlyClick}
            disabled={loading}
            role="menuitem"
          >
            <Icon name="Users" className="tab-privacy-fab-menu-icon" />
            <span>Followers Only</span>
          </button>
          <button
            className={`tab-privacy-fab-menu-item ${privacyLevel === 'private' ? 'active' : ''}`}
            onClick={handlePrivateClick}
            disabled={loading}
            role="menuitem"
          >
            <Icon name="Lock" className="tab-privacy-fab-menu-icon" />
            <span>Private</span>
          </button>
        </div>
      )}
    </div>
  );
};

export default TabPrivacySettings;

